<template>
    <div class="tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-p-4 tw-rounded">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h3 class="tw-font-bold tw-text-lg">{{ $t('common.cancellation-reasons') }}</h3>
            <div v-if="loading" class="tw-text-xs tw-text-gray-500">
                <icon icon="eos-icons:three-dots-loading" class="tw-text-lg" />
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="tw-space-y-3">
            <div v-for="n in 3" :key="n" class="tw-animate-pulse">
                <div class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                        <div class="tw-w-24 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                    </div>
                    <div class="tw-w-12 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="tw-text-center tw-py-6">
            <icon icon="solar:danger-circle-bold-duotone" class="tw-text-4xl tw-text-red-400 tw-mb-2" />
            <p class="tw-text-gray-600 tw-mb-3">{{ $t('common.failed-to-load') }}</p>
            <button @click="fetchData" 
                class="tw-px-4 tw-py-2 tw-bg-primary-500 tw-text-white tw-rounded tw-text-sm hover:tw-bg-primary-600 tw-transition-colors">
                {{ $t('buttons.retry') }}
            </button>
        </div>

        <!-- Empty State -->
        <div v-else-if="!cancellationReasons.length" class="tw-text-center tw-py-6">
            <icon icon="solar:check-circle-bold-duotone" class="tw-text-4xl tw-text-green-400 tw-mb-2 tw-mx-auto" />
            <p class="tw-text-gray-600">{{ $t('common.no-cancellations') }}</p>
            <p class="tw-text-sm tw-text-gray-500">{{ $t('common.all-orders-successful') }}</p>
        </div>

        <!-- Cancellation Reasons List -->
        <div v-else class="tw-space-y-2">
            <div 
                v-for="(reason, index) in cancellationReasons" 
                :key="reason.reason"
                class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded tw-border tw-border-gray-100 hover:tw-bg-gray-50 tw-transition-colors"
                :class="{ 'tw-bg-red-50 tw-border-red-200': index === 0 && reason.count > 0 }"
            >
                <div class="tw-flex tw-items-center tw-gap-3">
                    <!-- Rank Badge -->
                    <div class="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-text-xs tw-font-bold"
                         :class="getRankBadgeClass(reason.rank)">
                        {{ reason.rank }}
                    </div>
                    
                    <!-- Reason Icon and Label -->
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <icon :icon="getReasonIcon(reason.reason)" :class="getReasonIconClass(reason.reason)" />
                        <span class="tw-font-medium tw-text-gray-800">
                            {{ $t(`order-status.cancellation.${reason.reason}`) }}
                        </span>
                    </div>
                </div>

                <div class="tw-flex tw-items-center tw-gap-3">
                    <!-- Count -->
                    <span class="tw-font-bold tw-text-gray-700">{{ reason.count }}</span>
                    
                    <!-- Percentage -->
                    <div class="tw-text-xs tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded">
                        {{ getPercentage(reason.count) }}%
                    </div>
                </div>
            </div>

            <!-- Summary -->
            <div v-if="totalCancellations > 0" class="tw-mt-4 tw-pt-3 tw-border-t tw-border-gray-200">
                <div class="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-gray-600">
                    <span>{{ $t('common.total-cancellations') }}</span>
                    <span class="tw-font-semibold">{{ totalCancellations }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { getCancellationReasons } from '@/api/products'

// Injected from parent component
const id = inject('id')

// Component state
const loading = ref(true)
const error = ref(false)
const cancellationReasons = ref([])

// Computed properties
const totalCancellations = computed(() => 
    cancellationReasons.value.reduce((sum, reason) => sum + reason.count, 0)
)

// Get percentage of total cancellations
const getPercentage = (count) => {
    if (totalCancellations.value === 0) return 0
    return ((count / totalCancellations.value) * 100).toFixed(1)
}

// Get rank badge styling
const getRankBadgeClass = (rank) => {
    switch (rank) {
        case 1:
            return 'tw-bg-red-100 tw-text-red-700'
        case 2:
            return 'tw-bg-orange-100 tw-text-orange-700'
        case 3:
            return 'tw-bg-yellow-100 tw-text-yellow-700'
        default:
            return 'tw-bg-gray-100 tw-text-gray-700'
    }
}

// Get reason icon
const getReasonIcon = (reason) => {
    const iconMap = {
        'price-issue': 'solar:dollar-minimalistic-bold-duotone',
        'changed-mind': 'solar:mind-bold-duotone',
        'found-elsewhere': 'solar:shop-bold-duotone',
        'product-unavailable': 'solar:box-minimalistic-bold-duotone',
        'delivery-time': 'solar:clock-circle-bold-duotone',
        'shipping-cost': 'solar:delivery-bold-duotone',
        'wrong-order': 'solar:close-circle-bold-duotone',
        'other': 'solar:question-circle-bold-duotone'
    }
    return iconMap[reason] || 'solar:question-circle-bold-duotone'
}

// Get reason icon color class
const getReasonIconClass = (reason) => {
    const colorMap = {
        'price-issue': 'tw-text-red-500',
        'changed-mind': 'tw-text-blue-500',
        'found-elsewhere': 'tw-text-purple-500',
        'product-unavailable': 'tw-text-orange-500',
        'delivery-time': 'tw-text-yellow-600',
        'shipping-cost': 'tw-text-green-500',
        'wrong-order': 'tw-text-red-600',
        'other': 'tw-text-gray-500'
    }
    return colorMap[reason] || 'tw-text-gray-500'
}

// Fetch cancellation reasons data
const fetchData = async () => {
    if (!id) return
    
    loading.value = true
    error.value = false
    
    try {
        const response = await getCancellationReasons(id)
        
        if (response.data.code === 'SUCCESS') {
            cancellationReasons.value = response.data.data || []
        } else {
            throw new Error('API returned non-success code')
        }
    } catch (err) {
        console.error('Error fetching cancellation reasons:', err)
        error.value = true
        cancellationReasons.value = []
    } finally {
        loading.value = false
    }
}

// Initialize component
onMounted(() => {
    fetchData()
})
</script>

<style scoped>
/* Component-specific styles can be added here if needed */
</style> 